import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import AddActionInComponent from 'src/pages/DashboardPage/mytasks/addAction/inComponent';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import TGButton from 'src/components/ui/TGButton';
import { TooltipItem } from 'src/utils/Tooltip';
import TextAreaVPO from 'src/pages/DashboardPage/vpo/ui/textArea';
import { selectLastAddedId, selectLastDocId, selectIsLastAddedByCreator } from '../../store/addForms/selector';
import { addDataGOS, updateDataGOS } from '../../store/addForms/actions';

interface ViolationType {
  id: number;
  text: string;
  isSubmitted: boolean;
}

interface ViolationProps {
  value: string;
  isEdit?: boolean;
  index: number;
  formId?: number;
  violationId?: any;
  violationDocumentId?: string;
  disabled?: boolean;
}

function Violation({ value, isEdit, index, formId, violationId, violationDocumentId, disabled = false }: ViolationProps) {
  const dispatch = useAppDispatch();
  const componentId = useRef(`violation_${index}_${Date.now()}_${Math.random()}`).current;
  const idViolation = useAppSelector((state) => selectLastDocId(state, 'gos-violations'));
  const lastIdViolation = useAppSelector((state) => selectLastAddedId(state, 'gos-violations'));
  const isLastAddedByThisComponent = useAppSelector(
    (state) => selectIsLastAddedByCreator(state, 'gos-violations', componentId)
  );

  const [violationState, setViolationState] = useState({
    currentViolation: '',
    violations: [] as ViolationType[],
    savedViolationId: violationId || null as number | null,
    savedViolationDocumentId: violationDocumentId || null as string | null,
  });
  const { handleSubmit, control, setValue } = useForm({
    defaultValues: {
      violation: value || '',
    },
  });

  useEffect(() => {
    if (value) {
      setValue('violation', value);
    }
  }, [value, setValue]);

  useEffect(() => {
    if (violationId && value) {
      setViolationState(prev => ({
        ...prev,
        violations: [{
          id: violationId,
          text: value,
          isSubmitted: true,
        }],
      }));
    }
  }, [violationId, value]);

  const handleViolationSubmit = useCallback(
    (data: { violation: string }) => {
      if (!data.violation) return;

      const parentFormId = formId;
      const { savedViolationDocumentId, savedViolationId } = violationState;

      if (isEdit && savedViolationDocumentId) {
        //! Обновляем существующее нарушение
        const requestBody: any = { Title: 'Нарушение', ViolationText: data.violation, ParentFormId: parentFormId };
        dispatch(updateDataGOS('gos-violations', savedViolationDocumentId, requestBody));
        setViolationState(prev => ({
          ...prev,
          violations: prev.violations.map((v) => (v.id === savedViolationId ? { ...v, text: data.violation } : v))
        }));
      } else if (savedViolationDocumentId) {
        //! Обновляем только что созданное нарушение
        const requestBody: any = { Title: 'Нарушение upd', ViolationText: data.violation, ParentFormId: parentFormId };
        dispatch(updateDataGOS('gos-violations', savedViolationDocumentId, requestBody));
        setViolationState(prev => ({
          ...prev,
          violations: prev.violations.map((v) => (v.id === savedViolationId ? { ...v, text: data.violation } : v))
        }));
      } else {
        //! Создаем новое нарушение
        setViolationState(prev => ({ ...prev, currentViolation: data.violation }));
        const requestBody: any = { Title: 'Нарушение', ViolationText: data.violation, ParentFormId: parentFormId };
        dispatch(addDataGOS('gos-violations', requestBody, componentId));
      }
    },
    [isEdit, violationState, formId, dispatch, componentId]
  );

  useEffect(() => {
    if (!lastIdViolation || !isLastAddedByThisComponent) return;

    const { currentViolation } = violationState;

    setViolationState(prev => ({
      ...prev,
      savedViolationId: lastIdViolation,
      savedViolationDocumentId: idViolation,
    }));

    if (currentViolation) {
      setViolationState(prev => ({
        ...prev,
        violations: [
          ...prev.violations,
          {
            id: lastIdViolation,
            text: currentViolation,
            isSubmitted: true,
          }
        ],
        currentViolation: '',
      }));
      // setValue('violation', '');
    }
  }, [lastIdViolation, isLastAddedByThisComponent, idViolation, violationState.currentViolation, setValue]);

  const currentViolationId = violationId || violationState.savedViolationId;

  return (
    <div className='space-y-4'>
      {formId ? (
        <div className='p-5 bg-gray-100 rounded-lg'>
          <form onSubmit={handleSubmit(handleViolationSubmit)}>
            <Controller
              name='violation'
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <TextAreaVPO
                  dataField={{
                    type: 'textArea',
                    require: true,
                    width: 'w-full',
                    title: 'Нарушение',
                    key: 'violation',
                    placeholder: 'Опишите нарушение',
                  }}
                  value={field.value}
                  onChange={field.onChange}
                  errors={{ violation: error }}
                  minHeight={112}
                  disabled={disabled}
                />
              )}
            />
            <TGButton className='w-full mt-2' type='submit' disabled={disabled}>
              {isEdit ? 'Сохранить нарушение' : 'Сохранить нарушение'}
            </TGButton>
          </form>

          {violationState.violations.length > 0 ? (
            violationState.violations.map((violation) => (
              <div key={violation.id} className='mt-4'>
                <h3 className='text-[#637381] font-medium mb-2.5'>Действия</h3>
                <AddActionInComponent typeModule='Предписание' idModule={violation.id} CustomStyles='bg-white p-5' customTypes={`add_action_in_component_${violation.id}`} />
              </div>
            ))
          ) : currentViolationId ? (
            <div className='mt-4'>
              <h3 className='text-[#637381] font-medium mb-4'>Действия</h3>
              <AddActionInComponent typeModule='Предписание' idModule={currentViolationId} CustomStyles='bg-white p-5' customTypes={`add_action_in_component_${currentViolationId}`} />
            </div>
          ) : (
            <div className='mt-4'>
              <h3 className='text-[#637381] font-medium mb-4'>Действия</h3>
              <AddActionInComponent typeModule='Предписание' idModule={0} CustomStyles='bg-white p-5' customTypes={undefined} />
            </div>
          )}
        </div>
      ) : (
        <div className='p-5 bg-gray-100 rounded-lg'>
          <Controller
            name='violation'
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState: { error } }) => (
              <TooltipItem wrapperClassName='w-full' position='top' tooltipsText={'Сохраните форму, чтобы добавить нарушения и действия'}>
                <TextAreaVPO
                  dataField={{
                    type: 'textArea',
                    require: true,
                    width: 'w-full',
                    title: 'Нарушение',
                    key: 'violation',
                    placeholder: 'Опишите нарушение',
                    disabled: true,
                  }}
                  value={field.value}
                  onChange={field.onChange}
                  errors={{ violation: error }}
                  minHeight={112}
                  disabled={disabled}
                />
              </TooltipItem>
            )}
          />
          <TooltipItem wrapperClassName='w-full' position='top' tooltipsText={'Сохраните форму, чтобы добавить нарушения и действия'}>
            <TGButton className='w-full mt-2' type='button' disabled={true}>
              Сохранить нарушение
            </TGButton>
          </TooltipItem>
          <div className='mt-4'>
            <h3 className='text-[#637381] font-medium mb-4'>Действия</h3>
            <AddActionInComponent typeModule='Предписание' idModule={0} CustomStyles='bg-white p-5' customTypes={undefined} />
          </div>
        </div>
      )}
    </div>
  );
}

export default Violation;
